import React, { useState, useEffect, useCallback } from "react";
import Topbar from "../Components/Topbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addNumber,
  listNumbers,
  updateNumber,
  getPoolStats,
  listActiveSessions,
  getSession,
  forceExpireSession,
  getCallHistory,
  getCallDetail,
  getCallRecordings,
  fetchRecordingAudio,
  clickToCall,
  getWebhookConfig,
} from "../Config/callMaskingService";
import { formatEasternDateTime } from "../Utils/dateTime";

// ─── Enums ──────────────────────────────────────────────

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "numbers", label: "Masked Numbers" },
  { key: "sessions", label: "Active Sessions" },
  { key: "calls", label: "Call History" },
  { key: "webhooks", label: "Webhooks" },
];

const PROVIDERS = ["INFOBIP", "TWILIO", "VONAGE", "OTHER"];
const NUMBER_TYPES = ["LOCAL", "TOLL_FREE"];
const STATUSES = ["ACTIVE", "INACTIVE", "MAINTENANCE"];
const ALLOWED_TYPES = ["ORDER", "MARKETING", "ADMIN", "OTHER"];
const SESSION_TYPES = ["ORDER", "MARKETING", "ADMIN", "OTHER"];
const CALL_STATUSES = ["completed", "busy", "no-answer", "failed", "canceled"];

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  INACTIVE: "bg-gray-100 text-gray-800",
  MAINTENANCE: "bg-amber-100 text-amber-800",
};

const PROVIDER_COLORS = {
  TWILIO: "bg-red-50 text-red-700",
  INFOBIP: "bg-orange-50 text-orange-700",
  VONAGE: "bg-purple-50 text-purple-700",
  OTHER: "bg-gray-100 text-gray-700",
};

const SESSION_TYPE_COLORS = {
  ORDER: "bg-blue-50 text-blue-700",
  MARKETING: "bg-pink-50 text-pink-700",
  ADMIN: "bg-violet-50 text-violet-700",
  OTHER: "bg-gray-100 text-gray-700",
};

const CALL_STATUS_COLORS = {
  completed: "bg-emerald-50 text-emerald-700",
  busy: "bg-amber-50 text-amber-700",
  "no-answer": "bg-orange-50 text-orange-700",
  failed: "bg-red-50 text-red-700",
  canceled: "bg-gray-100 text-gray-600",
};

// ─── Helpers ────────────────────────────────────────────

function fmtDate(d) {
  return formatEasternDateTime(d);
}

function fmtTimeLeft(expiresAt) {
  if (!expiresAt) return "—";
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return "Expired";
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function fmtDuration(sec) {
  if (!sec) return "—";
  const s = parseInt(sec, 10);
  if (isNaN(s)) return sec;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function Spinner({ size = 6 }) {
  return (
    <div className={`inline-block w-${size} h-${size} border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin`} />
  );
}

function DetailRow({ label, value, mono, badge, badgeColor }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-sm font-medium text-gray-500 w-36 flex-shrink-0">{label}</span>
      {badge ? (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${badgeColor || "bg-gray-100 text-gray-700"}`}>
          {value ?? "—"}
        </span>
      ) : (
        <span className={`text-sm text-gray-900 break-all ${mono ? "font-mono" : ""}`}>{value ?? "—"}</span>
      )}
    </div>
  );
}

function AllowedTypeTags({ types }) {
  if (!types || types.length === 0) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span key={t} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SESSION_TYPE_COLORS[t] || "bg-gray-100 text-gray-600"}`}>
          {t}
        </span>
      ))}
    </div>
  );
}

// ─── Pagination ─────────────────────────────────────────

function Pagination({ meta, page, setPage }) {
  if (!meta || meta.totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50/50">
      <p className="text-sm text-gray-600">
        Page {meta.page} of {meta.totalPages} ({meta.total} total)
      </p>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <button
          disabled={page >= meta.totalPages}
          onClick={() => setPage(page + 1)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ─── Pool Stats Card ────────────────────────────────────

function PoolStatsCard({ stats, loading }) {
  const items = [
    { label: "Total", value: stats?.total, color: "text-gray-900", bg: "bg-white" },
    { label: "Active", value: stats?.active, color: "text-emerald-700", bg: "bg-emerald-50" },
    { label: "Inactive", value: stats?.inactive, color: "text-gray-600", bg: "bg-gray-50" },
    { label: "Maintenance", value: stats?.maintenance, color: "text-amber-700", bg: "bg-amber-50" },
    { label: "Bound Now", value: stats?.currentlyBound, color: "text-blue-700", bg: "bg-blue-50" },
    { label: "Live Calls", value: stats?.liveNow, color: "text-violet-700", bg: "bg-violet-50" },
    { label: "Available", value: stats?.available, color: "text-teal-700", bg: "bg-teal-50" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
      {items.map((it) => (
        <div
          key={it.label}
          className={`${it.bg} rounded-xl border border-gray-200 p-4 text-center transition-shadow hover:shadow-md`}
        >
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{it.label}</p>
          {loading ? (
            <div className="h-8 flex items-center justify-center"><Spinner size={5} /></div>
          ) : (
            <p className={`text-2xl font-bold ${it.color}`}>{it.value ?? "—"}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Webhook Config Panel ───────────────────────────────

function WebhookUrlRow({ label, url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Failed to copy"); }
  };
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-sm font-medium text-gray-600 w-36 flex-shrink-0">{label}</span>
      <code className="flex-1 text-sm bg-gray-100 rounded-lg px-3 py-2 text-gray-800 font-mono break-all border border-gray-200">
        {url}
      </code>
      <button
        onClick={copy}
        className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          copied ? "bg-emerald-100 text-emerald-700" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
        }`}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function WebhookConfigPanel({ webhookData, loading }) {
  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner size={8} /></div>;
  }
  const twilio = webhookData?.twilio;
  const infobip = webhookData?.infobip;

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">Configuration Required</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Set these webhook URLs in your provider dashboard for call masking to work.
            </p>
          </div>
        </div>
      </div>

      {twilio && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <h3 className="text-base font-semibold text-gray-900">Twilio</h3>
          </div>
          <div className="space-y-1">
            <WebhookUrlRow label="Incoming Call" url={twilio.incomingCallUrl} />
            <WebhookUrlRow label="Call Status" url={twilio.callStatusUrl} />
          </div>
        </div>
      )}

      {infobip && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-orange-500 rounded-full" />
            <h3 className="text-base font-semibold text-gray-900">Infobip</h3>
          </div>
          <div className="space-y-1">
            <WebhookUrlRow label="Incoming Call" url={infobip.incomingCallUrl} />
            <WebhookUrlRow label="Call Status" url={infobip.callStatusUrl} />
          </div>
        </div>
      )}

      {!twilio && !infobip && (
        <p className="text-gray-500 text-sm text-center py-8">No webhook config available from server.</p>
      )}
    </div>
  );
}

// ─── Add Number Modal ───────────────────────────────────

function AddNumberModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    phoneNumber: "", provider: "INFOBIP", numberType: "LOCAL", notes: "", allowedTypes: ["ORDER"],
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const toggleAllowedType = (t) => {
    setForm((prev) => {
      const arr = prev.allowedTypes.includes(t)
        ? prev.allowedTypes.filter((x) => x !== t)
        : [...prev.allowedTypes, t];
      return { ...prev, allowedTypes: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phoneNumber.trim()) { toast.error("Phone number is required"); return; }
    setSaving(true);
    try {
      const res = await addNumber({
        phoneNumber: form.phoneNumber.trim(),
        provider: form.provider,
        numberType: form.numberType,
        notes: form.notes.trim() || null,
        allowedTypes: form.allowedTypes.length > 0 ? form.allowedTypes : undefined,
      });
      if (res?.data?.id || res?.message?.toLowerCase().includes("success")) {
        toast.success("Masked number added");
        setForm({ phoneNumber: "", provider: "INFOBIP", numberType: "LOCAL", notes: "", allowedTypes: ["ORDER"] });
        onSaved();
        onClose();
      } else {
        throw new Error(res?.error || res?.message || "Failed to add number");
      }
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10">
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Add Masked Number</h3>
          </div>
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                placeholder="+14155551234"
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={form.provider}
                  onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number Type</label>
                <select
                  value={form.numberType}
                  onChange={(e) => setForm((p) => ({ ...p, numberType: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  {NUMBER_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Allowed Types</label>
              <div className="flex flex-wrap gap-2">
                {ALLOWED_TYPES.map((t) => (
                  <button
                    key={t} type="button"
                    onClick={() => toggleAllowedType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                      form.allowedTypes.includes(t)
                        ? "bg-indigo-50 text-indigo-700 border-indigo-300"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Mumbai pool batch 1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {saving ? "Adding…" : "Add Number"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Number Modal ──────────────────────────────────

function EditNumberModal({ number, open, onClose, onSaved }) {
  const [form, setForm] = useState({ status: "ACTIVE", numberType: "LOCAL", allowedTypes: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && number) {
      setForm({
        status: number.status || "ACTIVE",
        numberType: number.number_type || "LOCAL",
        allowedTypes: Array.isArray(number.allowed_types) ? [...number.allowed_types] : [],
      });
    }
  }, [open, number]);

  if (!open || !number) return null;

  const toggleAllowedType = (t) => {
    setForm((prev) => {
      const arr = prev.allowedTypes.includes(t)
        ? prev.allowedTypes.filter((x) => x !== t)
        : [...prev.allowedTypes, t];
      return { ...prev, allowedTypes: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {};
      if (form.status !== number.status) body.status = form.status;
      if (form.numberType !== number.number_type) body.numberType = form.numberType;
      const origTypes = (number.allowed_types || []).slice().sort().join(",");
      const newTypes = form.allowedTypes.slice().sort().join(",");
      if (origTypes !== newTypes) body.allowedTypes = form.allowedTypes;

      if (Object.keys(body).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      const res = await updateNumber(number.phone_number, body);
      if (res?.data || res?.message?.toLowerCase().includes("updated")) {
        toast.success("Number updated");
        onSaved();
        onClose();
      } else {
        throw new Error(res?.message || "Failed to update");
      }
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10">
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Edit Number</h3>
            <p className="text-sm text-gray-500 font-mono mt-0.5">{number.phone_number}</p>
          </div>
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number Type</label>
                <select
                  value={form.numberType}
                  onChange={(e) => setForm((p) => ({ ...p, numberType: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  {NUMBER_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Allowed Types</label>
              <div className="flex flex-wrap gap-2">
                {ALLOWED_TYPES.map((t) => (
                  <button
                    key={t} type="button"
                    onClick={() => toggleAllowedType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                      form.allowedTypes.includes(t)
                        ? "bg-indigo-50 text-indigo-700 border-indigo-300"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Session Detail Modal ───────────────────────────────

function SessionDetailModal({ sessionId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expiring, setExpiring] = useState(false);

  useEffect(() => {
    if (open && sessionId) {
      setLoading(true);
      getSession(sessionId)
        .then((res) => setData(res?.data || res))
        .catch(() => toast.error("Failed to load session"))
        .finally(() => setLoading(false));
    } else { setData(null); }
  }, [open, sessionId]);

  const handleExpire = async () => {
    if (!window.confirm("Force expire this session?")) return;
    setExpiring(true);
    try {
      const res = await forceExpireSession(sessionId);
      if (res?.message?.toLowerCase().includes("success") || res?.data) {
        toast.success("Session expired");
        onClose(true);
      } else { throw new Error(res?.message || "Failed to expire"); }
    } catch (err) { toast.error(err.message); }
    finally { setExpiring(false); }
  };

  if (!open) return null;

  const mnr = data?.masked_number_rel;

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => onClose(false)} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
            <h3 className="text-lg font-semibold text-gray-900">Session Detail</h3>
            <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-5">
            {loading ? (
              <div className="flex justify-center py-8"><Spinner size={8} /></div>
            ) : data ? (
              <div className="space-y-3">
                <DetailRow label="Session ID" value={data.id} mono />
                <DetailRow label="Type" value={data.type} badge badgeColor={SESSION_TYPE_COLORS[data.type]} />
                <DetailRow label="Order ID" value={data.order_id} />
                <DetailRow label="Masked Number" value={data.masked_number} mono />
                <DetailRow label="Created" value={fmtDate(data.created_at)} />
                <DetailRow label="Expires" value={fmtDate(data.expires_at)} />
                <DetailRow label="Time Left" value={fmtTimeLeft(data.expires_at)} />

                {data.type !== "ORDER" && (
                  <>
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Participants</p>
                    </div>
                    <DetailRow label="Caller Phone" value={data.caller_phone} mono />
                    <DetailRow label="Callee Phone" value={data.callee_phone} mono />
                    <DetailRow label="Agent ID" value={data.agent_id} mono />
                  </>
                )}

                {data.order && (
                  <>
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Info</p>
                    </div>
                    <DetailRow label="Order ID" value={data.order.order_id} />
                    <DetailRow label="Order Status" value={data.order.status} badge badgeColor="bg-blue-50 text-blue-700" />
                    <DetailRow label="User ID" value={data.order.user_id} mono />
                    <DetailRow label="Worker ID" value={data.order.worker_id} mono />
                    <DetailRow label="Contractor ID" value={data.order.contractor_id} mono />
                  </>
                )}

                {mnr && (
                  <>
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Masked Number Info</p>
                    </div>
                    <DetailRow label="Provider" value={mnr.provider} badge badgeColor={PROVIDER_COLORS[mnr.provider]} />
                    <DetailRow label="Number Type" value={mnr.number_type?.replace("_", " ")} />
                    <DetailRow label="Status" value={mnr.status} badge badgeColor={STATUS_COLORS[mnr.status]} />
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-medium text-gray-500 w-36 flex-shrink-0">Allowed Types</span>
                      <AllowedTypeTags types={mnr.allowed_types} />
                    </div>
                    <DetailRow label="Last Used" value={fmtDate(mnr.last_used_at)} />
                  </>
                )}

                <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                  <button
                    onClick={handleExpire}
                    disabled={expiring}
                    className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                  >
                    {expiring ? "Expiring…" : "Force Expire Session"}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Session not found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Call Detail Modal ──────────────────────────────────

// Uses new API: GET .../recordings?audio=1&recordingSid={sid} with JWT
function RecordingPlayer({ callSid, recording, hasMultiple }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let url = null;
    setLoading(true);
    setError(null);
    fetchRecordingAudio(callSid, hasMultiple ? recording.sid : undefined)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setAudioUrl(url);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [callSid, recording?.sid, hasMultiple]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await fetchRecordingAudio(callSid, hasMultiple ? recording.sid : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recording-${recording.sid || callSid}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="py-2 text-xs text-gray-400">Loading audio…</div>;
  if (error) return <div className="py-2 text-xs text-red-600">Failed to load audio</div>;
  if (!audioUrl) return null;

  return (
    <div className="space-y-2">
      <audio controls className="w-full h-8" preload="metadata" src={audioUrl} />
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
      >
        {downloading ? "Downloading…" : "Download"}
      </button>
    </div>
  );
}

function CallDetailModal({ callSid, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recLoading, setRecLoading] = useState(false);

  useEffect(() => {
    if (open && callSid) {
      setLoading(true);
      getCallDetail(callSid)
        .then((res) => setDetail(res?.data || res))
        .catch(() => toast.error("Failed to load call detail"))
        .finally(() => setLoading(false));

      setRecLoading(true);
      getCallRecordings(callSid)
        .then((res) => setRecordings(res?.data || []))
        .catch(() => {})
        .finally(() => setRecLoading(false));
    } else {
      setDetail(null);
      setRecordings([]);
    }
  }, [open, callSid]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
            <h3 className="text-lg font-semibold text-gray-900">Call Detail</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-5">
            {loading ? (
              <div className="flex justify-center py-8"><Spinner size={8} /></div>
            ) : detail ? (
              <div className="space-y-3">
                <DetailRow label="Call SID" value={detail.sid} mono />
                <DetailRow label="From" value={detail.from} mono />
                <DetailRow label="To" value={detail.to} mono />
                <DetailRow label="Status" value={detail.status} badge badgeColor={CALL_STATUS_COLORS[detail.status]} />
                <DetailRow label="Direction" value={detail.direction} />
                <DetailRow label="Duration" value={fmtDuration(detail.duration)} />
                <DetailRow label="Start Time" value={fmtDate(detail.startTime)} />
                <DetailRow label="End Time" value={fmtDate(detail.endTime)} />
                <DetailRow label="Price" value={detail.price ? `${detail.price} ${detail.priceUnit || ""}` : "—"} />
                <DetailRow label="Forwarded From" value={detail.forwardedFrom} mono />
                <DetailRow label="Created" value={fmtDate(detail.dateCreated)} />

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recordings</p>
                  {recLoading ? (
                    <div className="flex justify-center py-4"><Spinner size={5} /></div>
                  ) : recordings.length === 0 ? (
                    <p className="text-sm text-gray-400">No recordings for this call.</p>
                  ) : (
                    <div className="space-y-3">
                      {recordings.map((rec) => (
                        <div key={rec.sid} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono text-gray-600">{rec.sid}</span>
                            <span className="text-xs text-gray-500">{fmtDuration(rec.duration)}</span>
                          </div>
                          <RecordingPlayer
                            callSid={callSid}
                            recording={rec}
                            hasMultiple={recordings.length > 1}
                          />
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">{fmtDate(rec.dateCreated)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Call not found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Click to Call Modal ────────────────────────────────

function ClickToCallModal({ open, onClose }) {
  const [targetPhone, setTargetPhone] = useState("");
  const [calling, setCalling] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleCall = async (e) => {
    e.preventDefault();
    if (!targetPhone.trim()) { toast.error("Target phone is required"); return; }
    setCalling(true);
    setResult(null);
    try {
      const res = await clickToCall(targetPhone.trim());
      if (res?.data?.callSid || res?.message?.toLowerCase().includes("initiated")) {
        setResult(res.data);
        toast.success("Call initiated — check your phone!");
      } else {
        throw new Error(res?.error || res?.message || "Failed to initiate call");
      }
    } catch (err) { toast.error(err.message); }
    finally { setCalling(false); }
  };

  const handleClose = () => {
    setTargetPhone("");
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10">
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Make a Call</h3>
            <p className="text-sm text-gray-500 mt-0.5">Initiate a masked call via Twilio</p>
          </div>
          <div className="px-6 py-5">
            {result ? (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <svg className="w-8 h-8 text-emerald-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm font-semibold text-emerald-800">Call initiated — you'll receive the call shortly</p>
                </div>
                <DetailRow label="Call SID" value={result.callSid} mono />
                <DetailRow label="Session ID" value={result.sessionId} mono />
                <DetailRow label="Masked Number" value={result.maskedNumber} mono />
                <DetailRow label="Target" value={result.to} mono />
                <DetailRow label="Status" value={result.status} badge badgeColor="bg-blue-50 text-blue-700" />
                <div className="flex justify-end pt-3">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCall} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Phone Number</label>
                  <input
                    type="text"
                    value={targetPhone}
                    onChange={(e) => setTargetPhone(e.target.value)}
                    placeholder="+19876543210"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Your phone number is read from your admin profile automatically.</p>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={handleClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                  <button type="submit" disabled={calling} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {calling ? "Initiating…" : "Call Now"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ─── Main Component ─────────────────────────────────────
// ═══════════════════════════════════════════════════════════

function CallMasking() {
  const [activeTab, setActiveTab] = useState("overview");

  // Pool stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Webhook
  const [webhookData, setWebhookData] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(true);

  // Numbers
  const [numbers, setNumbers] = useState([]);
  const [numbersMeta, setNumbersMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [numbersFilter, setNumbersFilter] = useState({ status: "", provider: "", numberType: "", search: "" });
  const [numbersPage, setNumbersPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsMeta, setSessionsMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsFilter, setSessionsFilter] = useState({ orderId: "", maskedNumber: "", type: "" });
  const [selectedSession, setSelectedSession] = useState(null);

  // Calls
  const [calls, setCalls] = useState([]);
  const [callsMeta, setCallsMeta] = useState({ count: 0 });
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsFilter, setCallsFilter] = useState({ phoneNumber: "", status: "", startDate: "", endDate: "" });
  const [selectedCall, setSelectedCall] = useState(null);
  const [showClickToCall, setShowClickToCall] = useState(false);
  const [callingBack, setCallingBack] = useState(null);

  // All numbers for the calls tab dropdown
  const [allNumbers, setAllNumbers] = useState([]);
  const [allNumbersLoading, setAllNumbersLoading] = useState(false);

  // ── Fetchers ──────────────────────────────────────

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getPoolStats();
      setStats(res?.data || res);
    } catch { toast.error("Failed to fetch pool stats"); }
    finally { setStatsLoading(false); }
  }, []);

  const fetchWebhook = useCallback(async () => {
    setWebhookLoading(true);
    try {
      const res = await getWebhookConfig();
      setWebhookData(res?.data || res);
    } catch { toast.error("Failed to fetch webhook config"); }
    finally { setWebhookLoading(false); }
  }, []);

  const fetchNumbers = useCallback(async (page = 1) => {
    setNumbersLoading(true);
    try {
      const res = await listNumbers({
        page, limit: 10,
        status: numbersFilter.status || undefined,
        provider: numbersFilter.provider || undefined,
        numberType: numbersFilter.numberType || undefined,
        search: numbersFilter.search || undefined,
      });
      setNumbers(res?.data || []);
      setNumbersMeta(res?.meta || { page, limit: 10, total: 0, totalPages: 1 });
    } catch { toast.error("Failed to fetch numbers"); }
    finally { setNumbersLoading(false); }
  }, [numbersFilter]);

  const fetchSessions = useCallback(async (page = 1) => {
    setSessionsLoading(true);
    try {
      const res = await listActiveSessions({
        page, limit: 10,
        orderId: sessionsFilter.orderId || undefined,
        maskedNumber: sessionsFilter.maskedNumber || undefined,
        type: sessionsFilter.type || undefined,
      });
      setSessions(res?.data || []);
      setSessionsMeta(res?.meta || { page, limit: 10, total: 0, totalPages: 1 });
    } catch { toast.error("Failed to fetch sessions"); }
    finally { setSessionsLoading(false); }
  }, [sessionsFilter]);

  const fetchAllNumbers = useCallback(async () => {
    setAllNumbersLoading(true);
    try {
      const res = await listNumbers({ limit: 500, provider: "TWILIO" });
      setAllNumbers(res?.data || []);
    } catch { /* silent — dropdown just stays empty */ }
    finally { setAllNumbersLoading(false); }
  }, []);

  const fetchCalls = useCallback(async () => {
    if (!callsFilter.phoneNumber && !callsFilter.from && !callsFilter.to) return;
    setCallsLoading(true);
    try {
      const res = await getCallHistory({
        phoneNumber: callsFilter.phoneNumber || undefined,
        status: callsFilter.status || undefined,
        startDate: callsFilter.startDate || undefined,
        endDate: callsFilter.endDate || undefined,
        limit: 50,
      });
      setCalls(res?.data || []);
      setCallsMeta(res?.meta || { count: (res?.data || []).length });
    } catch { toast.error("Failed to fetch call history"); }
    finally { setCallsLoading(false); }
  }, [callsFilter]);

  // ── Tab-based data loading ────────────────────────

  useEffect(() => {
    if (activeTab === "overview") fetchStats();
  }, [activeTab, fetchStats]);

  useEffect(() => {
    if (activeTab === "webhooks") fetchWebhook();
  }, [activeTab, fetchWebhook]);

  useEffect(() => {
    if (activeTab === "numbers") fetchNumbers(numbersPage);
  }, [activeTab, numbersPage, fetchNumbers]);

  useEffect(() => {
    if (activeTab === "sessions") fetchSessions(sessionsPage);
  }, [activeTab, sessionsPage, fetchSessions]);

  useEffect(() => {
    if (activeTab === "calls" && allNumbers.length === 0) fetchAllNumbers();
  }, [activeTab, allNumbers.length, fetchAllNumbers]);

  useEffect(() => {
    if (activeTab === "calls") fetchCalls();
  }, [activeTab, fetchCalls]);

  // ── Render ────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Call Masking</h1>
              <p className="text-sm text-gray-500">Manage masked numbers, sessions, calls & provider webhooks</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === tab.key
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW ═══════════════════════════════════ */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Number Pool Stats</h2>
              <button onClick={fetchStats} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Refresh</button>
            </div>
            <PoolStatsCard stats={stats} loading={statsLoading} />
          </div>
        )}

        {/* ═══ NUMBERS ════════════════════════════════════ */}
        {activeTab === "numbers" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={numbersFilter.status}
                  onChange={(e) => { setNumbersFilter((f) => ({ ...f, status: e.target.value })); setNumbersPage(1); }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">All</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Provider</label>
                <select
                  value={numbersFilter.provider}
                  onChange={(e) => { setNumbersFilter((f) => ({ ...f, provider: e.target.value })); setNumbersPage(1); }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">All</option>
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                <select
                  value={numbersFilter.numberType}
                  onChange={(e) => { setNumbersFilter((f) => ({ ...f, numberType: e.target.value })); setNumbersPage(1); }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">All</option>
                  {NUMBER_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <input
                  type="text"
                  value={numbersFilter.search}
                  onChange={(e) => { setNumbersFilter((f) => ({ ...f, search: e.target.value })); setNumbersPage(1); }}
                  placeholder="+1555…"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-40 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  + Add Number
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Phone Number</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Provider</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Allowed</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Bound</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Last Used</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {numbersLoading ? (
                      <tr><td colSpan={8} className="text-center py-12"><Spinner /></td></tr>
                    ) : numbers.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-500">No masked numbers found.</td></tr>
                    ) : (
                      numbers.map((num) => (
                        <tr key={num.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-gray-900 text-xs">{num.phone_number}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${PROVIDER_COLORS[num.provider] || "bg-gray-100 text-gray-700"}`}>
                              {num.provider}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700">{num.number_type?.replace("_", " ") || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block text-xs font-medium rounded-full px-2.5 py-0.5 ${STATUS_COLORS[num.status] || "bg-gray-100 text-gray-800"}`}>
                              {num.status}
                            </span>
                          </td>
                          <td className="px-4 py-3"><AllowedTypeTags types={num.allowed_types} /></td>
                          <td className="px-4 py-3">
                            {num.is_bound ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700">
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />Yes
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">No</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(num.last_used_at)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setEditingNumber(num)}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination meta={numbersMeta} page={numbersPage} setPage={setNumbersPage} />
            </div>

            <AddNumberModal
              open={showAddModal}
              onClose={() => setShowAddModal(false)}
              onSaved={() => { fetchNumbers(numbersPage); fetchStats(); }}
            />
            <EditNumberModal
              number={editingNumber}
              open={!!editingNumber}
              onClose={() => setEditingNumber(null)}
              onSaved={() => { fetchNumbers(numbersPage); fetchStats(); }}
            />
          </div>
        )}

        {/* ═══ SESSIONS ═══════════════════════════════════ */}
        {activeTab === "sessions" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Order ID</label>
                <input
                  type="text"
                  value={sessionsFilter.orderId}
                  onChange={(e) => setSessionsFilter((f) => ({ ...f, orderId: e.target.value }))}
                  placeholder="1234"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-28 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Masked Number</label>
                <input
                  type="text"
                  value={sessionsFilter.maskedNumber}
                  onChange={(e) => setSessionsFilter((f) => ({ ...f, maskedNumber: e.target.value }))}
                  placeholder="+1555…"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-36 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                <select
                  value={sessionsFilter.type}
                  onChange={(e) => setSessionsFilter((f) => ({ ...f, type: e.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">All</option>
                  {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <button
                onClick={() => { setSessionsPage(1); fetchSessions(1); }}
                className="px-4 py-2 text-sm font-medium bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Search
              </button>
              <div className="ml-auto">
                <button onClick={() => fetchSessions(sessionsPage)} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Refresh</button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Session ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Order / Caller</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Masked Number</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Created</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Time Left</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sessionsLoading ? (
                      <tr><td colSpan={7} className="text-center py-12"><Spinner /></td></tr>
                    ) : sessions.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-12 text-gray-500">No active sessions.</td></tr>
                    ) : (
                      sessions.map((s) => (
                        <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-[120px] truncate" title={s.id}>{s.id}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${SESSION_TYPE_COLORS[s.type] || "bg-gray-100 text-gray-700"}`}>
                              {s.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-900">
                            {s.type === "ORDER" ? (
                              <span className="font-medium">Order #{s.order_id}</span>
                            ) : (
                              <span className="font-mono">{s.caller_phone || "—"}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-800">{s.masked_number}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(s.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700">
                              {fmtTimeLeft(s.expires_at)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => setSelectedSession(s.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-800">View</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination meta={sessionsMeta} page={sessionsPage} setPage={setSessionsPage} />
            </div>

            <SessionDetailModal
              sessionId={selectedSession}
              open={!!selectedSession}
              onClose={(refresh) => { setSelectedSession(null); if (refresh) fetchSessions(sessionsPage); }}
            />
          </div>
        )}

        {/* ═══ CALLS ══════════════════════════════════════ */}
        {activeTab === "calls" && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-2">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-800">
                  Call history and click-to-call are <span className="font-semibold">Twilio-only</span> features. Calls involving non-Twilio numbers will return an error.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Masked Number</label>
                <select
                  value={callsFilter.phoneNumber}
                  onChange={(e) => setCallsFilter((f) => ({ ...f, phoneNumber: e.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-52 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">
                    {allNumbersLoading ? "Loading numbers…" : "Select a number"}
                  </option>
                  {allNumbers.map((n) => (
                    <option key={n.id} value={n.phone_number}>
                      {n.phone_number}{n.notes ? ` — ${n.notes}` : ""}{n.status !== "ACTIVE" ? ` (${n.status})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Call Status</label>
                <select
                  value={callsFilter.status}
                  onChange={(e) => setCallsFilter((f) => ({ ...f, status: e.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                >
                  <option value="">All</option>
                  {CALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={callsFilter.startDate}
                  onChange={(e) => setCallsFilter((f) => ({ ...f, startDate: e.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={callsFilter.endDate}
                  onChange={(e) => setCallsFilter((f) => ({ ...f, endDate: e.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => setShowClickToCall(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Call SID</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">From</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">To</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Direction</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Duration</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {callsLoading ? (
                      <tr><td colSpan={8} className="text-center py-12"><Spinner /></td></tr>
                    ) : calls.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-500">
                          {callsFilter.phoneNumber ? "No calls found for this number." : "Select a masked number above to view call history."}
                        </td>
                      </tr>
                    ) : (
                      calls.map((c) => {
                        const otherParty = callsFilter.phoneNumber
                          ? (c.from === callsFilter.phoneNumber ? c.to : c.from)
                          : (c.from || c.to);
                        const isCalling = callingBack === c.sid;

                        return (
                          <tr key={c.sid} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-[120px] truncate" title={c.sid}>{c.sid}</td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-900">{c.from}</td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-900">{c.to}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${CALL_STATUS_COLORS[c.status] || "bg-gray-100 text-gray-700"}`}>
                                {c.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">{c.direction}</td>
                            <td className="px-4 py-3 text-xs text-gray-700 font-medium">{fmtDuration(c.duration)}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{fmtDate(c.dateCreated)}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={async () => {
                                    if (!otherParty) { toast.error("No number to call"); return; }
                                    setCallingBack(c.sid);
                                    try {
                                      const res = await clickToCall(otherParty);
                                      if (res?.data?.callSid || res?.message?.toLowerCase().includes("initiated")) {
                                        toast.success(`Calling ${otherParty} — check your phone`);
                                      } else {
                                        throw new Error(res?.error || res?.message || "Call failed");
                                      }
                                    } catch (err) { toast.error(err.message); }
                                    finally { setCallingBack(null); }
                                  }}
                                  disabled={isCalling || !otherParty}
                                  title={`Call ${otherParty}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-40 transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  {isCalling ? "Calling…" : "Call"}
                                </button>
                                <button onClick={() => setSelectedCall(c.sid)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1">Detail</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {calls.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/50">
                  <p className="text-sm text-gray-600">{callsMeta.count} call{callsMeta.count !== 1 ? "s" : ""} found</p>
                </div>
              )}
            </div>

            <CallDetailModal
              callSid={selectedCall}
              open={!!selectedCall}
              onClose={() => setSelectedCall(null)}
            />
            <ClickToCallModal
              open={showClickToCall}
              onClose={() => setShowClickToCall(false)}
            />
          </div>
        )}

        {/* ═══ WEBHOOKS ═══════════════════════════════════ */}
        {activeTab === "webhooks" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Provider Webhook URLs</h2>
              <button onClick={fetchWebhook} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Refresh</button>
            </div>
            <WebhookConfigPanel webhookData={webhookData} loading={webhookLoading} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CallMasking;
